import dayjs, { Dayjs } from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { extractApiErrorMessage } from '../../../helpers/apiErrorMessage'
import { Enum } from '../../../common/constants'
import { centsToDollarsDisplay } from '../../displayFormatters'
import { QuickBooksInvoiceRelinkCandidate, QuickBooksInvoiceSyncStatus } from './quickBooksResyncApi'

dayjs.extend(relativeTime)

const NotAvailable = '—'

export const QboLinkStates = {
  Healthy: 'healthy',
  NeverSynced: 'neverSynced',
  MissingInQbo: 'missingInQbo',
  DifferentRealm: 'differentRealm',
} as const
export type QboLinkState = Enum<typeof QboLinkStates>

export type QuickBooksInvoiceRow = {
  key: string
  invoiceId: string
  invoiceNumber: string
  customerName: string
  qboDocNumber: string
  total: string
  balance: string
  cachedQboBalance: string
  lastSynced: string
  lastSyncedAge: string
  linkState: QboLinkState
  qboPaymentNotApplied: boolean
}

export type QboLinkStateTag = {
  label: string
  color: 'orange' | 'red'
}

const toLinkState = (invoice: QuickBooksInvoiceSyncStatus): QboLinkState => {
  if (invoice.linkedToDifferentRealm === true) {
    return QboLinkStates.DifferentRealm
  }
  if (invoice.qboInvoiceExists === false) {
    return QboLinkStates.MissingInQbo
  }
  if (invoice.lastSyncedAt === null) {
    return QboLinkStates.NeverSynced
  }
  return QboLinkStates.Healthy
}

export const getLinkStateTag = (state: QboLinkState): QboLinkStateTag | null => {
  if (state === QboLinkStates.NeverSynced) {
    return { label: 'Never synced', color: 'orange' }
  }
  if (state === QboLinkStates.MissingInQbo) {
    return { label: 'Missing in QBO', color: 'red' }
  }
  if (state === QboLinkStates.DifferentRealm) {
    return { label: 'Linked to a different QBO company', color: 'red' }
  }
  return null
}

const orDash = (value: string | null): string => {
  if (value === null || value.trim() === '') {
    return NotAvailable
  }
  return value
}

export const defaultInvoiceSyncStatus = (): QuickBooksInvoiceSyncStatus => ({
  invoiceId: '',
  invoiceNumber: '',
  customerName: '',
  qboInvoiceId: '',
  qboDocNumber: null,
  total: 0,
  balance: 0,
  cachedQboBalance: null,
  lastSyncedAt: null,
  qboInvoiceExists: true,
  linkedToDifferentRealm: false,
  qboPaymentNotApplied: false,
})

export const toInvoiceRows = (
  invoices: QuickBooksInvoiceSyncStatus[],
  now: Dayjs = dayjs()
): QuickBooksInvoiceRow[] => {
  return invoices.map(invoice => ({
    key: invoice.invoiceId,
    invoiceId: invoice.invoiceId,
    invoiceNumber: invoice.invoiceNumber,
    customerName: orDash(invoice.customerName),
    qboDocNumber: orDash(invoice.qboDocNumber),
    total: centsToDollarsDisplay(invoice.total) ?? NotAvailable,
    balance: centsToDollarsDisplay(invoice.balance) ?? NotAvailable,
    cachedQboBalance: centsToDollarsDisplay(invoice.cachedQboBalance) ?? NotAvailable,
    lastSynced: invoice.lastSyncedAt === null
      ? 'Never synced'
      : dayjs(invoice.lastSyncedAt).format('MMM D, YYYY h:mm A'),
    lastSyncedAge: invoice.lastSyncedAt === null ? '' : dayjs(invoice.lastSyncedAt).from(now),
    linkState: toLinkState(invoice),
    qboPaymentNotApplied: invoice.qboPaymentNotApplied,
  }))
}

export const filterInvoiceRows = (rows: QuickBooksInvoiceRow[], searchText: string): QuickBooksInvoiceRow[] => {
  const needle = searchText.trim().toLowerCase()
  if (needle === '') {
    return rows
  }
  return rows.filter(row =>
    row.invoiceNumber.toLowerCase().includes(needle) ||
    row.customerName.toLowerCase().includes(needle) ||
    row.qboDocNumber.toLowerCase().includes(needle)
  )
}

export const getNoSearchMatchesMessage = (searchText: string): string => {
  return `No invoices match "${searchText.trim()}". Clear the search to see every QuickBooks-linked invoice.`
}

export type RelinkCandidateRow = {
  key: string
  qboInvoiceId: string
  docNumber: string
  customerName: string
  total: string
  balance: string
  transactionDate: string
  isCurrentLink: boolean
  linkedToInvoiceNumber: string | null
  matchTags: string[]
}

export const defaultRelinkCandidate = (): QuickBooksInvoiceRelinkCandidate => ({
  qboInvoiceId: '',
  docNumber: '',
  customerName: '',
  total: 0,
  balance: 0,
  transactionDate: null,
  linkedToInvoiceNumber: null,
  matchesTotal: false,
  matchesInvoiceNumber: false,
  matchesOriginalDocNumber: false,
  matchesPrivateNote: false,
})

const toMatchTags = (candidate: QuickBooksInvoiceRelinkCandidate): string[] => {
  const tags: string[] = []
  if (candidate.matchesTotal) {
    tags.push('Same total')
  }
  if (candidate.matchesInvoiceNumber) {
    tags.push('Doc # contains invoice #')
  }
  if (candidate.matchesOriginalDocNumber) {
    tags.push('Matches original doc #')
  }
  if (candidate.matchesPrivateNote) {
    tags.push('Note references invoice')
  }
  return tags
}

export const toRelinkCandidateRows = (
  candidates: QuickBooksInvoiceRelinkCandidate[],
  currentQboInvoiceId: string
): RelinkCandidateRow[] => {
  return candidates.map(candidate => ({
    key: candidate.qboInvoiceId,
    qboInvoiceId: candidate.qboInvoiceId,
    docNumber: orDash(candidate.docNumber),
    customerName: orDash(candidate.customerName),
    total: centsToDollarsDisplay(candidate.total) ?? NotAvailable,
    balance: centsToDollarsDisplay(candidate.balance) ?? NotAvailable,
    transactionDate: candidate.transactionDate === null
      ? NotAvailable
      : dayjs(candidate.transactionDate).format('MMM D, YYYY'),
    isCurrentLink: candidate.qboInvoiceId === currentQboInvoiceId,
    linkedToInvoiceNumber: candidate.linkedToInvoiceNumber,
    matchTags: toMatchTags(candidate),
  }))
}

export type RankedCandidateRow = {
  key: string
  matchTags: string[]
}

export const getProposedCandidateId = (rows: RankedCandidateRow[]): string | undefined => {
  if (rows.length === 1) {
    return rows[0].key
  }
  if (rows.length > 1 && rows[0].matchTags.length > 0) {
    return rows[0].key
  }
  return undefined
}

export const getNoCandidatesMessage = (qboCustomerName: string): string => {
  if (qboCustomerName.trim() === '') {
    return 'The linked QuickBooks customer has no invoices in the connected QuickBooks company.'
  }
  return `QuickBooks customer ${qboCustomerName} has no invoices in the connected QuickBooks company.`
}

export const candidateListTruncatedMessage =
  'Not every QuickBooks invoice for this customer could be loaded — older invoices are missing from this list.'

export const getRelinkSuccessMessage = (invoiceNumber: string, docNumber: string): string => {
  return `Relinked invoice ${invoiceNumber} to QuickBooks invoice ${docNumber}. The cached balance will refresh in a moment.`
}

export const getQueuedMessage = (invoiceCount: number): string => {
  if (invoiceCount === 0) {
    return 'No QuickBooks-linked invoices were queued.'
  }
  if (invoiceCount === 1) {
    return 'Queued a re-sync for 1 invoice. Refresh the list in a moment to see the updated balance.'
  }
  return `Queued a re-sync for ${invoiceCount} invoices. Refresh the list in a moment to see the updated balances.`
}

export const getResyncErrorMessage = (error: unknown): string => {
  return extractApiErrorMessage(error, 'Failed to queue the QuickBooks re-sync. Please try again.')
}

export const getInvoicesErrorMessage = (error: unknown): string => {
  return extractApiErrorMessage(error, "Failed to load the tenant's QuickBooks invoices. Try again.")
}

export const getRelinkCandidatesErrorMessage = (error: unknown): string => {
  return extractApiErrorMessage(error, 'Failed to look up matching QuickBooks invoices. Try again.')
}

export const getRelinkErrorMessage = (error: unknown): string => {
  return extractApiErrorMessage(error, 'Failed to relink the invoice. Please try again.')
}
